import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { DOCUMENT, TitleCasePipe } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { DataserviceService } from '../dataservice.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { isCompanyAdminUser } from '../utils/company-admin.util';
import { ChatService } from '../services/chat.service';
import { UserSelectionListItem } from '../shared/user-selection-list/user-selection-list.component';

interface SystemMessageRule {
  id?: string;
  creatorId?: string | null;
  companyId?: string | null;
  providerId?: string | null;
  metricType: string;
  comparator: string;
  threshold: number;
  triggerHour: string;
  message: string;
  userIds: string[];
  audienceLabel?: string;
  lastRunAt?: string;
}

interface SelectUserOption {
  id: string;
  label: string;
  company?: string;
  email?: string;
  searchIndex: string;
  aliases?: string[];
}

interface ProviderOption {
  id: string;
  name: string;
  company?: string;
}

@Component({
  selector: 'app-system-messages',
  templateUrl: './system-messages.component.html',
  styleUrls: ['./system-messages.component.css']
})
export class SystemMessagesComponent implements OnInit, OnDestroy {
  form: FormGroup;
  rules: SystemMessageRule[] = [];
  availableUsers: SelectUserOption[] = [];
  filteredUsers: SelectUserOption[] = [];
  selectedRecipients: SelectUserOption[] = [];
  providerOptions: ProviderOption[] = [];
  hourOptions = this.generateTimeSlots();
  metricOptions = [
    { label: 'Sales', value: 'sales' },
    { label: 'Installs', value: 'installs' }
  ];
  comparisonOptions = [
    { label: '=', value: '=' },
    { label: '>', value: '>' }
  ];
  loadingRules = false;
  loadingUsers = false;
  providersLoading = false;
  savingRule = false;
  currentUser: any;
  currentUserPrimaryId: string | null = null;
  recipientSearchControl: FormControl;
  selectedRecipientIds = new Set<string>();
  editingRuleId: string | null = null;
  recipientListItems: UserSelectionListItem[] = [];
  readonly recipientSelectionChecker = (userId: string) =>
    this.isRecipientSelected(userId);
  private destroy$ = new Subject<void>();
  private userLookup = new Map<string, SelectUserOption>();
  private userIdAliasMap = new Map<string, string>();
  private providerLookup = new Map<string, ProviderOption>();
  private currentUserIdentifierSet = new Set<string>();
  private contextInitialized = false;
  private readonly titleCasePipe = new TitleCasePipe();
  readonly selectDropdownContainer = () => this.document.body;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private fb: FormBuilder,
    private dataService: DataserviceService,
    private message: NzMessageService,
    private chatService: ChatService,
  ) {
    this.form = this.fb.group({
      metricType: ['sales', Validators.required],
      comparison: ['=', Validators.required],
      providerId: [null, Validators.required],
      threshold: [1, [Validators.required, Validators.min(0)]],
      triggerHour: ['08:00', Validators.required],
      message: ['', [Validators.required, Validators.maxLength(500)]],
      userIds: [[], Validators.required]
    });
    this.recipientSearchControl = this.fb.control('');

    this.recipientSearchControl.valueChanges
      .pipe(debounceTime(200), takeUntil(this.destroy$))
      .subscribe((value) => this.filterUsers(value));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    if (!isCompanyAdminUser(this.currentUser)) {
      this.message.error('You do not have access to system messages');
      return;
    }
    this.loadCurrentUserContext();
  }

  private bootstrapCurrentUserDirectoryEntry(): void {
    const preferredId =
      this.currentUserPrimaryId ?? this.extractPrimaryUserId(this.currentUser);
    if (preferredId) {
      this.currentUserPrimaryId = preferredId;
    }
    const primaryId = this.currentUserPrimaryId;
    const label = this.composeUserLabel(this.currentUser);
    this.captureCurrentUserIdentifiers(this.currentUser);
    if (primaryId) {
      const aliases = this.collectAliasIds(this.currentUser, primaryId);
      this.registerUserAliases(primaryId, aliases);
      this.ensureUserEntry(primaryId, label);
      this.registerCurrentUserIdentifier(primaryId);
      aliases.forEach((alias) => this.registerCurrentUserIdentifier(alias));
      return;
    }
    const fallbackId = this.normalizeIdValue(this.currentUser?.id);
    if (fallbackId) {
      this.registerCurrentUserIdentifier(fallbackId);
      this.ensureUserEntry(fallbackId, label);
    }
  }

  private loadCurrentUserContext(): void {
    this.dataService.getUser().subscribe({
      next: (profile) => {
        if (profile && typeof profile === 'object') {
          this.currentUser = { ...this.currentUser, ...profile };
          const preferredId = this.determinePreferredProfileId(profile);
          if (preferredId) {
            this.currentUserPrimaryId = preferredId;
            this.registerCurrentUserIdentifier(preferredId);
          }
          this.captureCurrentUserIdentifiers(profile);
        }
        this.initializeSystemMessagesContext();
      },
      error: () => {
        this.captureCurrentUserIdentifiers(this.currentUser);
        this.initializeSystemMessagesContext();
      },
    });
  }

  private initializeSystemMessagesContext(): void {
    if (this.contextInitialized) {
      return;
    }
    this.contextInitialized = true;
    this.bootstrapCurrentUserDirectoryEntry();
    this.loadUsers();
    this.loadProviders();
    this.loadRules();
  }

  loadUsers(): void {
    if (this.loadingUsers) {
      return;
    }

    const companyId = this.resolveCompanyId();
    if (!companyId) {
      this.message.error('Unable to determine your company. Contact an administrator.');
      return;
    }

    this.loadingUsers = true;
    this.chatService.listCompanyUsers(companyId).subscribe({
      next: (response: any) => {
        const list = this.extractUserRecords(response);
        this.userIdAliasMap.clear();
        this.availableUsers = list
          .map((user: any) => {
            const primaryId = this.extractPrimaryUserId(user);
            if (!primaryId) {
              return null;
            }
            const firstName = user.first_name || user.firstName || '';
            const lastName = user.last_name || user.lastName || '';
            const email = user.email || '';
            const company =
              user.company?.name ||
              user.company_name ||
              user.companyName ||
              user.company ||
              '';
            const label = this.composeUserLabel({
              first_name: firstName,
              last_name: lastName,
              email,
            }) || primaryId;
            const aliases = this.collectAliasIds(user, primaryId);
            this.registerUserAliases(primaryId, aliases);
            this.registerCurrentUserIdentifier(primaryId);
            aliases.forEach((alias) => this.registerCurrentUserIdentifier(alias));
            return {
              id: primaryId,
              label,
              company,
              email,
              searchIndex: `${label} ${company} ${email}`.toLowerCase(),
              aliases,
            } as SelectUserOption;
          })
          .filter(
            (option: SelectUserOption | null): option is SelectUserOption =>
              !!option,
          );

        this.userLookup = new Map(
          this.availableUsers.map((user) => [user.id, user]),
        );
        this.reconcileLegacyRuleIds();
        this.filterUsers(this.recipientSearchControl.value);
        this.syncSelectedRecipients();
        this.loadingUsers = false;
      },
      error: () => {
        this.message.error('Unable to load users');
        this.loadingUsers = false;
      }
    });
  }

  loadProviders(): void {
    if (this.providersLoading) {
      return;
    }

    const embeddedProviders = this.extractProvidersFromUser(this.currentUser);
    if (embeddedProviders.length) {
      this.applyProviderEntries(embeddedProviders);
    }

    const candidates = this.resolveCompanyIdentifierCandidates();
    if (!candidates.length) {
      if (!embeddedProviders.length) {
        this.providerOptions = [];
        this.providerLookup.clear();
      }
      return;
    }

    this.providersLoading = true;
    this.fetchProvidersByCompanyCandidates(candidates, 0);
  }

  private fetchProvidersByCompanyCandidates(
    candidates: string[],
    index: number,
  ): void {
    if (index >= candidates.length) {
      this.providersLoading = false;
      if (!this.providerOptions.length) {
        this.message.error('Unable to load providers');
      }
      return;
    }

    const companyId = candidates[index];
    this.chatService.listCompanyProviders(companyId).subscribe({
      next: (response: any) => {
        this.handleProviderResponse(response);
        this.providersLoading = false;
      },
      error: () => {
        this.fetchProvidersByCompanyCandidates(candidates, index + 1);
      },
    });
  }

  private handleProviderResponse(response: any): void {
    const rows = this.extractProviderRecords(response);
    const normalized: ProviderOption[] =
      rows
        .map((provider: any) => {
          const providerId = this.normalizeIdValue(
            provider?.id ?? provider?.provider_id ?? provider?.providerId,
          );
          if (!providerId) {
            return null;
          }
          const name = (
            provider?.name ??
            provider?.provider_name ??
            provider?.label ??
            ''
          )
            .toString()
            .trim();
          const companyName =
            provider?.company?.name ??
            provider?.company_name ??
            provider?.companyName ??
            '';
          return {
            id: providerId,
            name: name || `Provider ${providerId}`,
            company: companyName || undefined,
          } as ProviderOption;
        })
        .filter(
          (option: ProviderOption | null): option is ProviderOption =>
            !!option,
        );

    this.applyProviderEntries(normalized);
  }

  toggleRecipient(userId: string, forceState?: boolean): void {
    const normalized = this.resolveUserIdentifier(userId);
    if (!normalized) {
      return;
    }
    const shouldSelect =
      forceState !== undefined
        ? forceState
        : !this.selectedRecipientIds.has(normalized);

    if (shouldSelect) {
      this.selectedRecipientIds.add(normalized);
    } else {
      this.selectedRecipientIds.delete(normalized);
    }
    this.updateFormRecipients();
  }

  isRecipientSelected(userId: string): boolean {
    const normalized = this.resolveUserIdentifier(userId);
    if (!normalized) {
      return false;
    }
    return this.selectedRecipientIds.has(normalized);
  }

  removeRecipient(userId: string): void {
    this.toggleRecipient(userId, false);
  }

  private updateFormRecipients(): void {
    const control = this.form.get('userIds');
    if (!control) {
      return;
    }
    control.setValue(Array.from(this.selectedRecipientIds));
    this.syncSelectedRecipients();
  }

  private syncSelectedRecipients(): void {
    this.selectedRecipients = Array.from(this.selectedRecipientIds)
      .map((id) => this.userLookup.get(id))
      .filter((user): user is SelectUserOption => !!user);
  }

  private filterUsers(term: string = ''): void {
    const normalized = (term || '').toString().trim().toLowerCase();
    if (!normalized) {
      this.filteredUsers = [...this.availableUsers];
    } else {
      this.filteredUsers = this.availableUsers.filter((user) =>
        user.searchIndex.includes(normalized),
      );
    }
    this.recipientListItems = this.filteredUsers.map((user) =>
      this.mapRecipientToListItem(user),
    );
  }

  private mapRecipientToListItem(user: SelectUserOption): UserSelectionListItem {
    const meta: string[] = [];
    if (user.company) {
      meta.push(user.company);
    }
    if (user.email) {
      meta.push(user.email);
    }
    return {
      id: user.id,
      primaryText: this.formatUserLabel(user.label),
      meta,
    };
  }

  private clearRecipients(): void {
    this.selectedRecipientIds.clear();
    this.syncSelectedRecipients();
    this.form.get('userIds')?.setValue([]);
  }

  loadRules(): void {
    this.loadingRules = true;
    this.dataService.getSystemMessageRules().subscribe({
      next: (response: any) => {
        const rawRules = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
        this.rules = rawRules.map((rule: any) => this.normalizeRule(rule));
        this.primeDirectory(this.rules);
        this.reconcileLegacyRuleIds();
        this.loadingRules = false;
      },
      error: () => {
        this.message.error('Unable to load existing rules');
        this.loadingRules = false;
      }
    });
  }

  saveRule(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const companyId = this.resolveCompanyId();
    if (!companyId) {
      this.message.error('Unable to determine your company. Contact an administrator.');
      return;
    }

    const normalizedUserIds = this.normalizeRecipientList(this.form.value.userIds);
    const payload: any = {
      metricType: this.form.value.metricType,
      comparator: this.form.value.comparison,
      threshold: Number(this.form.value.threshold),
      triggerHour: this.form.value.triggerHour,
      message: (this.form.value.message || '').trim(),
      userIds: normalizedUserIds,
      providerId: this.form.value.providerId,
      companyId,
    };

    if (!this.editingRuleId) {
      const creatorId = this.resolveCreatorIdentifier();
      if (creatorId) {
        payload.creatorId = creatorId;
      }
    }

    this.savingRule = true;
    const request$ = this.editingRuleId
      ? this.dataService.updateSystemMessageRule(this.editingRuleId, payload)
      : this.dataService.createSystemMessageRule(payload);

    request$.subscribe({
      next: () => {
        this.message.success(
          this.editingRuleId ? 'System message rule updated' : 'System message rule saved',
        );
        this.resetForm();
        this.loadRules();
        this.savingRule = false;
      },
      error: () => {
        this.message.error(
          this.editingRuleId ? 'Unable to update rule' : 'Unable to save rule',
        );
        this.savingRule = false;
      },
    });
  }

  removeRule(rule: SystemMessageRule): void {
    if (!rule?.id) {
      return;
    }
    this.dataService.deleteSystemMessageRule(rule.id).subscribe({
      next: () => {
        this.message.success('Rule removed');
        this.loadRules();
      },
      error: () => {
        this.message.error('Unable to remove rule');
      }
    });
  }

  private resetForm(): void {
    this.form.reset({
      metricType: 'sales',
      comparison: '=',
      providerId: null,
      threshold: 1,
      triggerHour: '08:00',
      message: '',
      userIds: []
    });
    this.clearRecipients();
    this.recipientSearchControl.setValue('', { emitEvent: false });
    this.filterUsers('');
    this.editingRuleId = null;
  }

  private hasPermission(permission: string): boolean {
    if (this.currentUser?.super_admin) {
      return true;
    }
    return this.currentUser?.role?.permissions?.some((p: any) => p.name === permission) ?? false;
  }

  startEditRule(rule: SystemMessageRule): void {
    if (!rule?.id) {
      return;
    }
    this.editingRuleId = rule.id;
    const normalizedIds = this.normalizeRecipientList(rule.userIds);
    this.form.patchValue({
      metricType: rule.metricType,
      comparison: rule.comparator,
      providerId: rule.providerId || null,
      threshold: rule.threshold,
      triggerHour: rule.triggerHour,
      message: rule.message,
      userIds: normalizedIds,
    });
    this.selectedRecipientIds = new Set(normalizedIds);
    normalizedIds.forEach((userId) => this.ensureUserEntry(userId));
    if (rule.creatorId) {
      const label =
        this.isCurrentUserIdentifier(rule.creatorId)
          ? this.composeUserLabel(this.currentUser)
          : undefined;
      this.ensureUserEntry(rule.creatorId, label);
    }
    if (rule.providerId) {
      this.ensureProviderEntry(rule.providerId);
    }
    this.syncSelectedRecipients();
    this.recipientSearchControl.setValue('', { emitEvent: false });
  }

  cancelEditing(): void {
    if (!this.savingRule) {
      this.resetForm();
    }
  }

  private normalizeRule(payload: any): SystemMessageRule {
    if (!payload || typeof payload !== 'object') {
      return {
        id: undefined,
        metricType: 'sales',
        comparator: '=',
        threshold: 0,
        triggerHour: '00:00',
        message: '',
        userIds: [],
        audienceLabel: undefined,
        lastRunAt: undefined,
      };
    }

    return {
      id: payload.id,
      creatorId: payload.creatorId ?? payload.creator_id ?? null,
      companyId: payload.companyId ?? payload.company_id ?? undefined,
      providerId: payload.providerId ?? payload.provider_id ?? undefined,
      metricType: payload.metricType ?? payload.metric_type ?? 'sales',
      comparator: payload.comparator ?? '=',
      threshold: Number(payload.threshold ?? 0),
      triggerHour: payload.triggerHour ?? payload.trigger_hour ?? '00:00',
      message: payload.message ?? '',
      userIds: this.normalizeRecipientList(
        Array.isArray(payload.userIds)
          ? payload.userIds
          : Array.isArray(payload.user_ids)
          ? payload.user_ids
          : [],
      ),
      audienceLabel: payload.audienceLabel ?? payload.audience_label ?? undefined,
      lastRunAt: payload.lastRunAt ?? payload.last_run_at ?? undefined,
    };
  }

  private primeDirectory(rules: SystemMessageRule[]): void {
    rules.forEach((rule) => {
      rule.userIds.forEach((userId) => {
        this.ensureUserEntry(userId);
      });
      if (rule.creatorId) {
        const label =
          this.isCurrentUserIdentifier(rule.creatorId)
            ? this.composeUserLabel(this.currentUser)
            : undefined;
        this.ensureUserEntry(rule.creatorId, label);
      }
      if (rule.providerId) {
        this.ensureProviderEntry(rule.providerId);
      }
    });
    this.syncSelectedRecipients();
  }

  getRecipientLabel(userId: string): string {
    return this.getUserLabel(userId);
  }

  getUserLabel(userId?: string | null): string {
    if (!userId) {
      return '—';
    }
    const normalizedId = this.resolveUserIdentifier(userId);
    const user =
      this.userLookup.get(normalizedId) || this.userLookup.get(userId);
    if (!user) {
      return '—';
    }
    const normalized = (user.label || '').trim();
    if (
      !normalized ||
      normalized === userId ||
      normalized === `User ${userId}`
    ) {
      return '—';
    }
    return this.formatUserLabel(normalized);
  }

  getProviderLabel(providerId?: string | null): string {
    if (!providerId) {
      return '—';
    }
    const provider = this.providerLookup.get(providerId);
    if (!provider) {
      return providerId;
    }
    const label = provider.name?.trim();
    if (!label || label === providerId || label === `Provider ${providerId}`) {
      return providerId;
    }
    return label;
  }

  formatUserLabel(label: string): string {
    const trimmed = (label || '').trim();
    if (!trimmed) {
      return trimmed;
    }
    if (trimmed.includes('@')) {
      return trimmed;
    }
    return this.titleCasePipe.transform(trimmed);
  }

  private ensureUserEntry(userId?: string | null, label?: string): void {
    if (!userId) {
      return;
    }
    const existing = this.userLookup.get(userId);
    if (existing) {
      if (label && (existing.label.startsWith('User ') || existing.label === userId)) {
        this.userLookup.set(userId, { ...existing, label });
      }
      return;
    }
    const resolvedLabel = label ?? `User ${userId}`;
    const fallback: SelectUserOption = {
      id: userId,
      label: resolvedLabel,
      searchIndex: resolvedLabel.toLowerCase(),
    };
    this.userLookup.set(userId, fallback);
    this.availableUsers = [...this.availableUsers, fallback];
  }

  private ensureProviderEntry(providerId?: string | null, label?: string): void {
    if (!providerId) {
      return;
    }
    const existing = this.providerLookup.get(providerId);
    if (existing) {
      if (label && (existing.name.startsWith('Provider ') || existing.name === providerId)) {
        this.providerLookup.set(providerId, { ...existing, name: label });
      }
      return;
    }
    const fallback: ProviderOption = {
      id: providerId,
      name: label?.trim() || `Provider ${providerId}`,
    };
    this.providerLookup.set(providerId, fallback);
    this.providerOptions = [...this.providerOptions, fallback];
  }

  private applyProviderEntries(providers: ProviderOption[]): void {
    this.providerOptions = providers;
    this.providerLookup = new Map(providers.map((provider) => [provider.id, provider]));

    const currentProvider = this.form.get('providerId')?.value;
    if (currentProvider) {
      this.ensureProviderEntry(currentProvider);
    }
    this.rules.forEach((rule) => {
      if (rule.providerId && !this.providerLookup.has(rule.providerId)) {
        this.ensureProviderEntry(rule.providerId);
      }
    });
  }

  private composeUserLabel(user: any): string {
    if (!user) {
      return '';
    }
    const first = user.first_name || user.firstName || '';
    const last = user.last_name || user.lastName || '';
    const full = [first, last].filter(Boolean).join(' ').trim();
    if (full) {
      return full;
    }
    if (user.email) {
      return user.email;
    }
    return user.id || '';
  }

  private generateTimeSlots(): { label: string; value: string }[] {
    const slots: { label: string; value: string }[] = [];
    for (let hour = 0; hour < 24; hour += 1) {
      for (let minutes = 0; minutes < 60; minutes += 5) {
        const label = `${hour.toString().padStart(2, '0')}:${minutes
          .toString()
          .padStart(2, '0')}`;
        slots.push({ label, value: label });
      }
    }
    return slots;
  }

  private extractPrimaryUserId(user: any): string | null {
    return (
      this.normalizeIdValue(user?.employee_id) ??
      this.normalizeIdValue(user?.employeeId) ??
      this.normalizeIdValue(user?.user_code) ??
      this.normalizeIdValue(user?.userCode) ??
      this.normalizeIdValue(user?.id) ??
      this.normalizeIdValue(user?.userId) ??
      this.normalizeIdValue(user?.user_uuid) ??
      this.normalizeIdValue(user?.user_id) ??
      null
    );
  }

  private extractUserRecords(payload: any): any[] {
    if (Array.isArray(payload)) {
      return payload;
    }
    const candidates = [
      payload?.data,
      payload?.data?.users,
      payload?.data?.items,
      payload?.users,
      payload?.records,
      payload?.items,
      payload?.results,
      payload?.rows,
    ];
    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return candidate;
      }
    }
    return [];
  }

  private extractProviderRecords(payload: any): any[] {
    if (Array.isArray(payload)) {
      return payload;
    }
    const candidates = [
      payload?.data,
      payload?.providers,
      payload?.items,
      payload?.records,
      payload?.results,
      payload?.rows,
    ];
    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return candidate;
      }
    }
    return [];
  }

  private extractProvidersFromUser(user: any): ProviderOption[] {
    if (!user) {
      return [];
    }
    const sources = Array.isArray(user.providers) ? user.providers : [];
    const normalized: ProviderOption[] = [];
    const seen = new Set<string>();

    sources.forEach((provider: any) => {
      const providerId = this.normalizeIdValue(
        provider?.id ??
          provider?.provider_id ??
          provider?.providerId ??
          provider?.uuid,
      );
      if (!providerId || seen.has(providerId)) {
        return;
      }
      seen.add(providerId);
      const name = (
        provider?.name ??
        provider?.provider_name ??
        provider?.label ??
        ''
      )
        .toString()
        .trim();
      normalized.push({
        id: providerId,
        name: name || `Provider ${providerId}`,
        company:
          provider?.company?.name ??
          provider?.company_name ??
          provider?.companyName ??
          undefined,
      });
    });

    return normalized;
  }

  private collectAliasIds(user: any, primaryId: string): string[] {
    const candidates = [
      this.normalizeIdValue(user?.user_id),
      this.normalizeIdValue(user?.legacy_user_id),
      this.normalizeIdValue(user?.employee_id),
      this.normalizeIdValue(user?.employeeId),
      this.normalizeIdValue(user?.user_code),
      this.normalizeIdValue(user?.userCode),
      this.normalizeIdValue(user?.userId),
      this.normalizeIdValue(user?.id),
    ];
    const aliases: string[] = [];
    candidates.forEach((candidate) => {
      if (candidate && candidate !== primaryId && !aliases.includes(candidate)) {
        aliases.push(candidate);
      }
    });
    return aliases;
  }

  private registerUserAliases(primaryId: string, aliases: string[] = []): void {
    if (!primaryId) {
      return;
    }
    this.userIdAliasMap.set(primaryId, primaryId);
    aliases.forEach((alias) => {
      if (alias && alias !== primaryId) {
        this.userIdAliasMap.set(alias, primaryId);
      }
    });
  }

  private resolveCreatorIdentifier(): string | null {
    if (this.currentUserPrimaryId) {
      return this.currentUserPrimaryId;
    }
    const fallback =
      this.normalizeIdValue(this.currentUser?.employee_id) ??
      this.normalizeIdValue(this.currentUser?.employeeId) ??
      this.normalizeIdValue(this.currentUser?.user_code) ??
      this.normalizeIdValue(this.currentUser?.userCode) ??
      this.normalizeIdValue(this.currentUser?.id);
    if (!fallback) {
      return null;
    }
    const resolved = this.resolveUserIdentifier(fallback);
    return resolved || fallback;
  }

  private isCurrentUserIdentifier(candidate?: string | null): boolean {
    const normalized = this.normalizeIdValue(candidate);
    if (!normalized) {
      return false;
    }
    if (this.currentUserIdentifierSet.has(normalized)) {
      return true;
    }
    const resolvedCandidate = this.resolveUserIdentifier(normalized);
    if (resolvedCandidate && this.currentUserIdentifierSet.has(resolvedCandidate)) {
      return true;
    }
    const current = this.resolveCreatorIdentifier();
    if (current) {
      return (resolvedCandidate || normalized) === current;
    }
    return false;
  }

  private resolveUserIdentifier(value: any): string {
    const normalized = this.normalizeIdValue(value);
    if (!normalized) {
      return '';
    }
    return this.userIdAliasMap.get(normalized) ?? normalized;
  }

  private normalizeRecipientList(values: any[] | undefined | null): string[] {
    const seen = new Set<string>();
    const normalized: string[] = [];
    (values ?? []).forEach((value) => {
      const resolved = this.resolveUserIdentifier(value);
      if (resolved && !seen.has(resolved)) {
        seen.add(resolved);
        normalized.push(resolved);
      }
    });
    return normalized;
  }

  private reconcileLegacyRuleIds(): void {
    if (!this.rules.length || !this.userIdAliasMap.size) {
      return;
    }

    let mutated = false;
    this.rules = this.rules.map((rule) => {
      const normalizedIds = this.normalizeRecipientList(rule.userIds);
      if (!this.areArraysEqual(rule.userIds, normalizedIds)) {
        mutated = true;
        return {
          ...rule,
          userIds: normalizedIds,
        };
      }
      return rule;
    });

    if (mutated) {
      this.rules = [...this.rules];
      if (this.editingRuleId) {
        const editedRule = this.rules.find((rule) => rule.id === this.editingRuleId);
        if (editedRule) {
          const normalizedIds = this.normalizeRecipientList(editedRule.userIds);
          this.selectedRecipientIds = new Set(normalizedIds);
          this.form.get('userIds')?.setValue(normalizedIds, { emitEvent: false });
        }
      } else {
        this.selectedRecipientIds = new Set(
          Array.from(this.selectedRecipientIds).map((id) => this.resolveUserIdentifier(id)),
        );
        this.form.get('userIds')?.setValue(
          Array.from(this.selectedRecipientIds),
          { emitEvent: false },
        );
      }
      this.primeDirectory(this.rules);
      this.syncSelectedRecipients();
    }
  }

  private captureCurrentUserIdentifiers(user: any): void {
    const candidates = [
      this.normalizeIdValue(user?.user_id),
      this.normalizeIdValue(user?.employee_id),
      this.normalizeIdValue(user?.employeeId),
      this.normalizeIdValue(user?.user_code),
      this.normalizeIdValue(user?.userCode),
      this.normalizeIdValue(user?.user_id),
      this.normalizeIdValue(user?.userId),
      this.normalizeIdValue(user?.id),
      this.normalizeIdValue(user?.legacy_user_id),
    ];
    candidates.forEach((candidate) => this.registerCurrentUserIdentifier(candidate));
  }

  private registerCurrentUserIdentifier(value?: string | null): void {
    const normalized = this.normalizeIdValue(value);
    if (normalized) {
      this.currentUserIdentifierSet.add(normalized);
    }
  }

  private isKnownCurrentUserIdentifier(value?: string | null): boolean {
    const normalized = this.normalizeIdValue(value);
    if (!normalized) {
      return false;
    }
    if (this.currentUserIdentifierSet.has(normalized)) {
      return true;
    }
    const resolved = this.resolveUserIdentifier(normalized);
    return !!resolved && this.currentUserIdentifierSet.has(resolved);
  }

  private areArraysEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) {
      return false;
    }
    return a.every((value, index) => value === b[index]);
  }

  private resolveCompanyId(): string | null {
    const candidates = this.resolveCompanyIdentifierCandidates();
    return candidates.length ? candidates[0] : null;
  }

  private resolveCompanyIdentifierCandidates(): string[] {
    const rawCandidates = [
      this.currentUser?.company_id,
      this.currentUser?.companyId,
      this.currentUser?.company?.id,
      this.currentUser?.company?.company_id,
      this.currentUser?.company?.companyId,
      this.currentUser?.company?.uuid,
      this.currentUser?.company?.code,
      this.currentUser?.company,
    ];
    const normalized = rawCandidates
      .map((candidate) => this.normalizeIdValue(candidate))
      .filter((value): value is string => !!value);
    return Array.from(new Set(normalized));
  }

  private determinePreferredProfileId(profile: any): string | null {
    return (
      this.normalizeIdValue(profile?.user_id) ??
      this.normalizeIdValue(profile?.employee_id) ??
      this.normalizeIdValue(profile?.employeeId) ??
      this.normalizeIdValue(profile?.user_code) ??
      this.normalizeIdValue(profile?.userCode) ??
      this.normalizeIdValue(profile?.id) ??
      null
    );
  }

  private normalizeIdValue(value: any): string | null {
    if (value === undefined || value === null) {
      return null;
    }
    const str = String(value).trim();
    return str.length ? str : null;
  }
}
